import { useEffect, lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router';
import '../src/assets/js/common.js';

// Global vendor stylesheets.
//
// These MUST be imported here at the app entry rather than inside a component.
// They previously lived in pages/includes/header.js, which meant webpack saw
// them enter the graph via a lazily-loaded route chunk - so their position
// relative to CSS Modules (e.g. PrimaryButton.module.css) differed per chunk.
// mini-css-extract-plugin then emitted a "Conflicting order" warning, which
// fails any build run with CI=true.
import 'bootstrap/dist/css/bootstrap.min.css';
import './assets/css/fontawesome/css/all.min.css';
import 'swiper/css';
import 'lenis/dist/lenis.css';
import 'aos/dist/aos.css';

import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import Lenis from 'lenis';
import { SettingProvider } from './settingInfoContext.js';
import AOS from 'aos';
import SplashScreen from './components/SplashScreen/SplashScreen.js';

// Lazy Loading Pages
const Home = lazy(() => import('./pages/home.js'));
const About = lazy(() => import('./pages/about.js'));
const Campus = lazy(() => import('./pages/campus.js'));
const Learn = lazy(() => import('./pages/learn.js'));
const Stream = lazy(() => import('./pages/stream.js'));
const Contact = lazy(() => import('./pages/contact.js'));
const Blogs = lazy(() => import('./pages/blogs.js'));
const BlogDetails = lazy(() => import('./pages/blog-details.js'));
const Insights = lazy(() => import('./pages/insights.js'));
const Faq = lazy(() => import('./pages/faq.js'));

// SEO landing pages
const Compare = lazy(() => import('./pages/compare.js'));
const CompareDetails = lazy(() => import('./pages/compare-details.js'));
const WhyVedcool = lazy(() => import('./pages/why-vedcool.js'));
const Features = lazy(() => import('./pages/features.js'));
const FeatureDetails = lazy(() => import('./pages/feature-details.js'));
const Solutions = lazy(() => import('./pages/solutions.js'));
const SolutionDetails = lazy(() => import('./pages/solution-details.js'));
const Locations = lazy(() => import('./pages/locations.js'));
const LocationDetails = lazy(() => import('./pages/location-details.js'));
const Pricing = lazy(() => import('./pages/pricing.js'));
const Demo = lazy(() => import('./pages/demo.js'));
const MigrateFromTeachmint = lazy(() => import('./pages/migrate-from-teachmint.js'));
const Glossary = lazy(() => import('./pages/glossary.js'));
const GuideDetails = lazy(() => import('./pages/guide-details.js'));

const TermsOfUse = lazy(() => import('./pages/terms-of-use.js'));
const PrivacyPolicy = lazy(() => import('./pages/privacy-policy.js'));
const DoonPrivacyPolicy = lazy(() => import('./pages/doon-privacy-policy.js'));
const DeleteAccount = lazy(() => import('./pages/delete-account.js'));
const Disclaimer = lazy(() => import('./pages/disclaimer.js'));
const NotFound = lazy(() => import('./pages/404.js'));
const ScrollToTop = lazy(() => import('./components/ScrollToTop/ScrollToTop.js'));

export default function App() {

  useEffect(() => {
    AOS.init({
      duration: 1000,
      mirror: true,
      once: false,
      debounceDelay: 10
    });
  }, []);

  // Initialize Lenis
  useEffect(() => {
    const lenis = new Lenis({
      autoRaf: true,
    });

    // Make Lenis accessible globally for ScrollToTop component
    window.lenisInstance = lenis;

    // Cleanup function
    return () => {
      if (window.lenisInstance) {
        window.lenisInstance.destroy();
        delete window.lenisInstance;
      }
    };
  }, []);

  return (
    <>
      {/*
        Rendered outside SettingProvider and eagerly imported (not lazy) so it
        paints on the first frame, before any route chunk resolves.
      */}
      <SplashScreen />

      <SettingProvider>
        <Suspense fallback={<div style={{ height: '100%', width: '100%', backgroundColor: '#fff', overflow: 'hidden', display: 'block' }} />}>
          <ScrollToTop />
          <Routes>
            <Route key="home" path="/" element={<Home pageName="Home" />} />
            <Route key="about" path="/about" element={<About pageName="About" />} />

            {/* Products */}
            <Route key="campus" path="/campus" element={<Campus pageName="Campus" />} />
            <Route key="learn" path="/learn" element={<Learn pageName="Learn" />} />
            <Route key="stream" path="/stream" element={<Stream pageName="Stream" />} />

            <Route key="blogs" path="/blogs" element={<Blogs pageName="Blogs" />} />
            <Route key="insights" path="/insights" element={<Insights pageName="Insights" />} />
            <Route key="blog-details" path="/blogs/:slug" element={<BlogDetails pageName="Blog Details" />} />
            <Route key="faq" path="/faq" element={<Faq pageName="FAQ" />} />

            {/* SEO landing pages */}
            <Route key="why-vedcool" path="/why-vedcool" element={<WhyVedcool pageName="Why Vedcool" />} />
            <Route key="compare" path="/compare" element={<Compare pageName="Compare" />} />
            <Route key="compare-details" path="/compare/:slug" element={<CompareDetails pageName="Compare" />} />
            <Route key="features" path="/features" element={<Features pageName="Features" />} />
            <Route key="feature-details" path="/features/:slug" element={<FeatureDetails pageName="Features" />} />
            <Route key="solutions" path="/solutions" element={<Solutions pageName="Solutions" />} />
            <Route key="solution-details" path="/solutions/:slug" element={<SolutionDetails pageName="Solutions" />} />
            <Route key="locations" path="/locations" element={<Locations pageName="Locations" />} />
            <Route key="location-details" path="/locations/:slug" element={<LocationDetails pageName="Locations" />} />
            <Route key="pricing" path="/pricing" element={<Pricing pageName="Pricing" />} />
            <Route key="demo" path="/demo" element={<Demo pageName="Book a Demo" />} />
            <Route key="migrate-from-teachmint" path="/migrate-from-teachmint" element={<MigrateFromTeachmint pageName="Migrate from Teachmint" />} />
            <Route key="glossary" path="/resources/glossary" element={<Glossary pageName="Glossary" />} />
            <Route key="guide-details" path="/guides/:slug" element={<GuideDetails pageName="Guide" />} />

            <Route key="contact" path="/contact" element={<Contact pageName="Contact" />} />

            <Route key="terms-of-use" path="/terms-of-use" element={<TermsOfUse pageName="Terms of Use" />} />
            <Route key="privacy-policy" path="/privacy-policy" element={<PrivacyPolicy pageName="Privacy Policy" />} />
            <Route key="doon-privacy-policy" path="/doon-privacy-policy" element={<DoonPrivacyPolicy pageName="Privacy Policy" />} />
            <Route key="disclaimer" path="/disclaimer" element={<Disclaimer pageName="Disclaimer" />} />
            {/* Declared as the data-deletion URL in Google Play Console, so this
                path must stay stable and reachable without auth. */}
            <Route key="delete-account" path="/delete-account" element={<DeleteAccount pageName="Delete Account" />} />

            <Route key="404" path="*" element={<NotFound pageName="404" />} />
          </Routes>
        </Suspense>
      </SettingProvider>
    </>
  );
}